import { representedModels, TargetShape, TargetView } from './model-target';
import { ITab } from '../interfaces/ITab';
import { AgentWorkspaceSource } from './interfaces/agent-session';

/** Read-only metadata boundary. The existing tab/Creator still owns its data. */
export interface WorkspaceDocumentMetadata {
  connection: { dataService: { data_service_id: string } };
  model_UUID?: string;
  hierarchy?: string;
  name_value?: string;
}

export interface WorkspaceMetadataOwner {
  readonly agentContextRevision: number;
  readonly savedDocument?: WorkspaceDocumentMetadata;
  readonly viewFromServer?: { viewUUID: string; file_name: string; model_UUID?: string; hierarchy?: string };
  readonly isLoading: boolean;
  readonly canvasItems?: readonly TargetShape[];
  readonly viewCanvas?: readonly TargetView[];
  readonly currentFocus?: { getShape(): TargetShape[] };
  readonly currentConnectionFocus?: { getConnection(): { iFrom: TargetShape; iTo?: TargetShape } | null | undefined };
  readonly agentSimulationSnapshot?: AgentWorkspaceSource['context']['simulation'];
}

export function workspaceSource(tab: ITab, owner: WorkspaceMetadataOwner | null,
                                pending?: WorkspaceDocumentMetadata): AgentWorkspaceSource {
  const document = owner?.savedDocument;
  // NEW_TAB_CONNECTION already captures SharedService.default_database_connection.
  // Never substitute the mutable globally selected Data Service here.
  const connection = document?.connection ?? pending?.connection;
  const view = owner?.viewFromServer;
  // The live Canvas is authoritative. A reused Creator used to retain view and
  // focus metadata after its shapes were cleared, incorrectly making it ambiguous.
  const explicitlyEmptyCanvas = owner?.canvasItems?.length === 0;
  const views = explicitlyEmptyCanvas ? [] : (owner?.viewCanvas ?? []);
  // Restored Views carry their canonical outer Model identity in the persisted
  // View record. The Canvas reconstruction can be asynchronous, so use that
  // trusted record only when it is the sole available representation source.
  const persistedIdentity: TargetView[] = view?.model_UUID && view.hierarchy
    && view.model_UUID !== '__MULTIPLE__' && view.hierarchy !== '__MULTIPLE__'
    ? [{ model_UUID: view.model_UUID, hierarchy: view.hierarchy, view: [] }] : [];
  const effectiveViews = representedModels(views).length ? views : persistedIdentity;
  const represented = representedModels(effectiveViews);
  // A renderer focus can be retained from an earlier import or export. It is
  // not an explicit agent decision, so it must never silently choose one
  // Model in a multi-Model Creator. Structured Choice is the only agent-side
  // disambiguation path; it is stored by AgentWorkspaceRegistry afterwards.
  const target = represented.length === 1
    ? { state: 'resolved' as const, modelUuid: represented[0].modelUuid, hierarchy: represented[0].hierarchy }
    : represented.length > 1
      ? { state: 'ambiguous' as const }
      : { state: 'empty' as const };
  // A restored View's saved document uses its View title as name_value. Never
  // present that title as an unverified canonical Model name.
  const modelName = owner?.viewFromServer ? (represented.length > 1 ? 'Multiple Models' : undefined)
    : (target.state === 'resolved' ? document?.name_value || undefined : represented.length > 1 ? 'Multiple Models' : undefined);
  return { id: tab.id, instance: owner, lifecycleRevision: owner?.agentContextRevision ?? 0,
    context: { tabType: tab.type, label: tab.label,
      dataServiceId: connection?.dataService?.data_service_id,
      modelUuid: target.state === 'resolved' ? target.modelUuid : undefined,
      modelTargetState: target.state,
      hierarchy: target.state === 'resolved' ? target.hierarchy : undefined,
      representedModels: represented,
      modelName,
      simulation: owner?.agentSimulationSnapshot,
      viewUuid: view?.viewUUID || null, viewName: view?.file_name || undefined,
      loading: !owner || owner.isLoading } };
}
